import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLanguage } from '../contexts/LanguageContext';

const SITE_URL = 'https://sreglobaltrade.com';

const SEO = ({ 
  title = 'SRE GLOBAL - Trading Platform, Insurance & Tourism Services',
  description = 'SRE GLOBAL - Switzerland-based financial services. FCA regulated. Yetkili aracı kurum. Trading platform, insurance, tourism, mutual funds, portfolio management and investment advisory.',
  keywords,
  ogImage,
  path = ''
}) => {
  const { t, language } = useLanguage();
  
  const defaultKeywords = t.keywords || 'SRE GLOBAL, Yetkili Aracı Kurum, sreglobaltrade, SRE GLOBAL TRADING PLATFORM, SRE GLOBAL insurance, SRE GLOBAL Tourism, trading platform, fund investment, insurance services, tourism, Swiss Re, portfolio management, investment advisory, capital markets';
  const finalKeywords = keywords || defaultKeywords;

  const pagePath = path || window.location.pathname;
  const canonicalUrl = `${SITE_URL}${pagePath}`;
  const finalOgImage = ogImage || `${SITE_URL}/sre-global-logo.png`;

  const langMap = {
    en: 'en_US',
    tr: 'tr_TR',
    de: 'de_DE',
    fr: 'fr_FR'
  };

  // Manage canonical and hreflang via useEffect for reliability
  useEffect(() => {
    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Update hreflang tags
    const langs = ['en', 'tr', 'de', 'fr'];
    langs.forEach(lang => {
      let tag = document.querySelector(`link[hreflang="${lang}"]`);
      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', 'alternate');
        tag.setAttribute('hreflang', lang);
        document.head.appendChild(tag);
      }
      tag.setAttribute('href', `${canonicalUrl}${canonicalUrl.includes('?') ? '&' : '?'}lang=${lang}`);
    });

    // x-default
    let xDefault = document.querySelector('link[hreflang="x-default"]');
    if (!xDefault) {
      xDefault = document.createElement('link');
      xDefault.setAttribute('rel', 'alternate');
      xDefault.setAttribute('hreflang', 'x-default');
      document.head.appendChild(xDefault);
    }
    xDefault.setAttribute('href', canonicalUrl);
  }, [canonicalUrl]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    "name": "SRE GLOBAL",
    "alternateName": ["SRE GLOBAL", "sreglobaltrade"],
    "description": description,
    "url": SITE_URL,
    "logo": `${SITE_URL}/sre-global-logo.png`,
    "parentOrganization": {
      "@type": "Organization",
      "name": "SWISS RE"
    },
    "areaServed": "Worldwide",
    "knowsLanguage": ["en", "tr", "de", "fr"],
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Financial License",
        "name": "Yetkili Araci Kurum (Authorized Brokerage Firm)",
        "recognizedBy": {
          "@type": "Organization",
          "name": "Yetkili Araci Kurum"
        }
      },
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Financial License",
        "name": "UK FCA Authorization",
        "recognizedBy": {
          "@type": "Organization",
          "name": "UK Financial Conduct Authority (FCA)"
        }
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "SRE GLOBAL Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "SRE GLOBAL TRADING PLATFORM",
            "description": "White label trading solutions and B2B brokerage technology"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "SRE GLOBAL Insurance",
            "description": "Comprehensive insurance and protection services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "SRE GLOBAL Tourism",
            "description": "Premium travel and tourism experiences"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "SRE GLOBAL Mutual Funds",
            "description": "Authorized mutual fund investment services"
          }
        }
      ]
    }
  };

  return (
    <Helmet>
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={finalKeywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SRE GLOBAL" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:locale" content={langMap[language] || 'en_US'} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalOgImage} />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
